// AdminBookings.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import "./../../index.css";
import BookingTable from "./BookingTable";
import BookingForm from "./BookingForm";
import Loader from "../../Loader";
import moment from "moment";
import { useMutation } from "@apollo/client";
import { GET_BOOKINGS } from "./graphql";
import CreateBooking from "./CreateBooking/CreateBooking";

/* ----------------------------
   Helpers (normalize/serialize filter)
   ---------------------------- */

const normalizeToDateString = (val) => {
  if (!val) return null;

  // If moment object
  if (typeof val === "object" && moment.isMoment(val)) {
    return val.format("YYYY-MM-DD");
  }

  // If already "YYYY-MM-DD"
  const s = String(val);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // If timestamp or ISO or another date format
  const m = moment(s);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

const serializeFilter = (f = {}) => {
  if (f.bookingId || f.contactNumber) {
    return {
      bookingId: f.bookingId || null,
      contactNumber: f.contactNumber || null,
    };
  }

  const statusArr = Array.isArray(f.status) ? f.status.filter(Boolean) : [];
  const uniqueSortedStatus = Array.from(new Set(statusArr.map(String))).sort();

  const empIds = Array.isArray(f.employeeIds)
    ? f.employeeIds.filter(Boolean)
    : [];

  return {
    employeeIds: empIds.length ? empIds : undefined,
    status: uniqueSortedStatus,
    verificationStatus: f.verificationStatus || null,
    queryStartDate: normalizeToDateString(f.queryStartDate),
    queryEndDate: normalizeToDateString(f.queryEndDate),
    issueType: f.issueType || null,
    hasRemark: f.hasRemark || null,
    // pass through as boolean false (filter) or undefined (no filter)
    isAdminAcknowledged: f.isAdminAcknowledged === false ? false : undefined,
    isDummyEmployee: typeof f.isDummyEmployee === "boolean" ? f.isDummyEmployee : undefined,
    isFromBlockedUser: typeof f.isFromBlockedUser === "boolean" ? f.isFromBlockedUser : undefined,
  };
};


const getInitialFilter = (isUpcoming) =>
  isUpcoming
    ? {
        status: [
          "Pending",
          "Confirmed",
          "Headed",
          "Reached",
          "Started",
          "Scanned",
        ],
        queryStartDate: moment().format("YYYY-MM-DD"),
        queryEndDate: moment().add(7, "days").format("YYYY-MM-DD"),
        verificationStatus: null,
        issueType: null,
        hasRemark: null
      }
    : {
        status: [
          "Pending",
          "Confirmed",
          "Headed",
          "Reached",
          "Started",
          "Scanned",
          "Completed",
          "Cancelled",
          "Declined",
        ],
        queryStartDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
        queryEndDate: moment().format("YYYY-MM-DD"),
        verificationStatus: null,
        issueType: null,
        hasRemark: null
      };

/* ----------------------------
   AdminBookings component
   ---------------------------- */
const AdminBookings = () => {
  const [data, setData] = useState([]);
  const [isUpcomingBooking, setIsUpcomingBooking] = useState(true);
  const [upcomingFilter, setUpcomingFilter] = useState(getInitialFilter(true));
  const [previousFilter, setPreviousFilter] = useState(getInitialFilter(false));
  const [editBookingForm, setEditBookingForm] = useState(false);
  const [editingData, setEditingData] = useState(null); // default null
  const [isFetched, setIsFetched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creatingBooking, setCreatingBooking] = useState(false);

  const currentFilter = isUpcomingBooking ? upcomingFilter : previousFilter;

  const [getBookingsMutation] = useMutation(GET_BOOKINGS);

  // dedupe & debounce
  const lastFetchFilterRef = useRef(null);
  const fetchTimerRef = useRef(null);

  // Atomic toggle that updates both the boolean and the derived bookingFilter
  const applyUpcomingToggle = useCallback((upcoming) => {
    setIsUpcomingBooking(upcoming);
  }, []);

  // open edit form helper: sets editingData then opens form.
  // We set editingData first; adding a tiny defer to ensure child sees editingData.
  const openEditForm = useCallback((booking) => {
    setEditingData(booking || null);
    // defer opening so editingData is committed before mounting the child
    setTimeout(() => setEditBookingForm(true), 0);
  }, []);

  const closeEditForm = useCallback(() => {
    setEditBookingForm(false);
  }, []);

  const handleFilterChange = useCallback(
    (newFilter) => {
      if (isUpcomingBooking) {
        setUpcomingFilter(newFilter);
      } else {
        setPreviousFilter(newFilter);
      }
    },
    [isUpcomingBooking]
  );

  // merge updated order locally
  const handleOrderUpdated = useCallback((updatedOrder) => {
    console.log("Order updated:", updatedOrder);
    if (!updatedOrder) return;
    setData((prev) =>
      prev.map((b) =>
        b.id === updatedOrder.id ? { ...b, ...updatedOrder } : b
      )
    );
    setEditingData((prev) =>
      prev && prev.id === updatedOrder.id ? { ...prev, ...updatedOrder } : prev
    );
  }, []);

  const loadBookings = useCallback(
    async (filter, forceRefresh = false) => {
      setLoading(true);
      try {
        const serialized = serializeFilter(filter || {});
        const sNow = JSON.stringify(serialized);
        if (!forceRefresh && lastFetchFilterRef.current === sNow) {
          setLoading(false);
          setIsFetched(true);
          return;
        }
        lastFetchFilterRef.current = sNow;
        const res = await getBookingsMutation({
          variables: { data: serialized },
        });
        if (res?.data?.getBookings?.status === "SUCCESS") {
          setData(res.data.getBookings.orders || []);
        } else {
          setData([]);
          if (res?.data?.getBookings?.message) alert(res.data.getBookings.message);
        }
      } catch (err) {
        console.error("Failed to load bookings", err);
        setData([]);
      } finally {
        setIsFetched(true);
        setLoading(false);
      }
    },
    [getBookingsMutation]
  );

  // debounce and run fetch when bookingFilter changes
  useEffect(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      loadBookings(currentFilter);
      fetchTimerRef.current = null;
    }, 50);
    return () => {
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = null;
      }
    };
  }, [currentFilter, loadBookings]);

  if (!isFetched) return <Loader />;

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        {editBookingForm ? (
          <BookingForm
            // closeEditForm is boolean close handler
            setEditBookingForm={closeEditForm}
            editingData={editingData}
            onBookingUpdated={handleOrderUpdated}
          />
        ) : creatingBooking ? (
          <CreateBooking setCreateBookingForm={setCreatingBooking} />
        ) : (
          <BookingTable
            data={data}
            isUpcomingBooking={isUpcomingBooking}
            // pass atomic toggle
            setIsUpcomingBooking={applyUpcomingToggle}
            openEditForm={openEditForm} // explicit API: expects booking object
            // keep setEditingData if other child needs direct set (optional)
            setEditingData={setEditingData}
            setCreateBookingForm={setCreatingBooking}
            bookingFilter={currentFilter}
            setBookingFilter={handleFilterChange}
            loading={loading}
            onRefresh={() => loadBookings(currentFilter, true)}
            unacknowledgedCount={data.filter((b) => !b.isAdminAcknowledged).length}
          />
        )}
      </Layout>
    </div>
  );
};

export default AdminBookings;
